import { Controller } from "@hotwired/stimulus"
import { addonCostLine } from "../../utils/addon-cost-line.util"
import { purchaseAddon } from "../../utils/addon-purchase.util"
import { isAbortError } from "../../utils/fetch-error.util"

// The quantity modal for every quantity add-on (extra users, extra bank
// accounts). Steps the total, previews the per-period cost, posts the form as
// JSON and — when the card needs authentication — runs the 3-D Secure challenge
// through the same code the checkout uses, asks the server to re-read Stripe,
// and only then follows redirect_url (utils/addon-purchase.util.js, shared
// with the one-click billing--addon-purchase).
export default class extends Controller {
  static targets = ["input", "total", "warning", "decrementButton", "errors", "submitButton", "spinner"]
  static values = {
    unitPrice: Number,
    currencyFormat: { type: Object, default: {} },
    minQuantity: { type: Number, default: 0 },
    publishableKey: String,
    syncUrl: String,
    texts: { type: Object, default: {} }
  }

  connect() {
    this.updateTotal()
  }

  increment() {
    this.setQuantity(this.quantity() + 1)
  }

  decrement() {
    const next = this.quantity() - 1
    if (next < 0) return
    if (next < this.minQuantityValue) {
      this.showWarning()
      return
    }
    this.setQuantity(next)
  }

  updateTotal() {
    const qty = Math.max(this.quantity(), this.minQuantityValue)
    if (qty !== this.quantity()) this.inputTarget.value = qty
    this.hideWarning()
    this.totalTarget.textContent = this.costLine(qty)
    this.updateButtons()
  }

  async submit(event) {
    event.preventDefault()
    this.setLoading(true)
    this.showError("")
    let leaving = false

    try {
      const result = await purchaseAddon(this.element, {
        publishableKey: this.publishableKeyValue,
        syncUrl: this.syncUrlValue,
        texts: this.textsValue,
        tag: "addon-quantity"
      })
      if (!result.ok) {
        this.showError(result.error)
        return
      }
      if (!this.element.isConnected) return
      leaving = true
      this.navigate(result.redirectUrl)
    } catch (error) {
      if (isAbortError(error)) return
      console.error("[addon-quantity]", error)
      this.showError(error?.message || this.textsValue.genericError)
    } finally {
      // Leaving: keep Save disabled while the next page loads, so a second
      // click cannot post the same change again.
      if (!leaving) this.setLoading(false)
    }
  }

  // Separate so the spec can observe it: jsdom cannot assign location.href.
  navigate(url) {
    window.location.href = url
  }

  // The pattern comes from the server's own (untranslated) `cost_line` copy —
  // see `texts.costLine` built in _quantity_modal.html.erb — so a translator
  // reordering the sentence is honoured here too. Shared with the downgrade
  // keep-N stepper (utils/addon-cost-line.util.js) so both print one format.
  costLine(qty) {
    return addonCostLine({
      quantity: qty,
      unitPrice: this.unitPriceValue,
      currencyFormat: this.currencyFormatValue,
      period: this.textsValue.period,
      pattern: this.textsValue.costLine,
      zero: this.textsValue.zero
    })
  }

  quantity() {
    return Number.parseInt(this.inputTarget.value, 10) || 0
  }

  setQuantity(value) {
    this.inputTarget.value = value
    this.updateTotal()
  }

  updateButtons() {
    if (this.hasDecrementButtonTarget) this.decrementButtonTarget.disabled = this.quantity() <= 0
  }

  showWarning() {
    if (this.hasWarningTarget) this.warningTarget.classList.remove("d-none")
  }

  hideWarning() {
    if (this.hasWarningTarget) this.warningTarget.classList.add("d-none")
  }

  showError(message) {
    if (this.hasErrorsTarget) this.errorsTarget.textContent = message
  }

  setLoading(loading) {
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = loading
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("d-none", !loading)
  }
}
